import {getFilterConfig} from '../../../../../utils/filterManagementHelpers'

/**
 * Enrolled options for outreach display
 * Maps enrolledValue (0=No, 1=Yes, 2=All) to labels
 */
const enrolledOptions = [
  {value: 0, label: 'Not Enrolled'},
  {value: 1, label: 'Enrolled'},
  {value: 2, label: 'All'}
]

/**
 * Get selected states formatted for PDF chip display
 * @param {Object} stateData - State filter data from breadcrumbs
 * @returns {Array} Array of chip objects
 */
const getSelectedStates = (stateData) => {
  return stateData.default?.map((state) => {
    return {label: state === stateData.entireUnitedStateLabel ? 'Entire US' : state, type: 'string'}
  })
}

/**
 * Get custom filter name from customizations, sdohFilter, or filterConfig
 * Priority: customizations from API > sdohFilter (admin) > config name > key
 * @param {string} filterKey - Filter key
 * @param {Object} filterConfig - New filter config (filterConfig.config.json)
 * @param {Object} customizations - Custom names from filterManagementSlice
 * @returns {string} Custom name or config name or key
 */
const getFilterName = (filterKey, filterConfig, customizations) => {
  if (customizations?.filterNames?.[filterKey]) {
    return customizations.filterNames[filterKey]
  }

  const config = getFilterConfig(filterKey, filterConfig)
  return config?.name || filterKey
}

/**
 * Get toggle value label
 * @param {string|number} value - Toggle value ('0', '1', '2' or 0, 1, 2)
 * @returns {string} 'Yes', 'No', or 'All'
 */
const getToggleLabel = (value) => {
  const numValue = Number(value)
  if (numValue === 1) return 'Yes'
  if (numValue === 0) return 'No'
  return 'All'
}

/**
 * Check if object is empty
 * @param {Object} obj - Object to check
 * @returns {boolean} True if empty
 */
function isEmpty(obj) {
  if (!obj) return true
  return Object.keys(obj).length === 0
}

/**
 * Process range filter into chip format
 */
const processRangeFilter = (key, value, filterName) => {
  if (Array.isArray(value) && value.length === 2) {
    return {
      label: filterName,
      key,
      type: 'range',
      values: {min: value[0], max: value[1]}
    }
  }
  return null
}

/**
 * Process checkbox filter into chip format(s)
 */
const processCheckboxFilter = (key, value, filterName) => {
  if (!Array.isArray(value) || value.length === 0) return []

  // Handle County (stateCountyFipsCode) specially - display as string chips
  if (key === 'stateCountyFipsCode') {
    return value.map((countyValue) => ({
      type: 'string',
      label: countyValue
    }))
  }

  // Standard checkbox - one chip per selected value
  return value.map((selectedValue) => ({
    type: 'checkbox',
    label: filterName,
    values: selectedValue
  }))
}

/**
 * Process toggle filter into chip format
 */
const processToggleFilter = (key, value, filterName) => {
  // Skip "All" (2) as it means no filter applied
  if (value === '2' || value === 2) return null

  return {
    label: filterName,
    key,
    type: 'toggle',
    values: getToggleLabel(value)
  }
}

/**
 * Process a single filter into chip format(s)
 */
const processFilter = (key, value, metadata, filterConfig, customizations) => {
  // Skip outreach filters - handled separately
  if (metadata.type === 'outreach') return []

  // Skip if value is null/undefined
  if (value === null || value === undefined) return []

  const filterName = getFilterName(key, filterConfig, customizations)

  switch (metadata.type) {
    case 'range': {
      const rangeChip = processRangeFilter(key, value, filterName)
      return rangeChip ? [rangeChip] : []
    }

    case 'checkbox':
      return processCheckboxFilter(key, value, filterName)

    case 'toggle': {
      const toggleChip = processToggleFilter(key, value, filterName)
      return toggleChip ? [toggleChip] : []
    }

    default:
      return [{type: 'string', label: `${filterName}: ${value}`}]
  }
}

/**
 * Format filter data for PDF chip display using new filter management system
 *
 * @param {Object} appliedFilters - Applied filters from filterManagementSlice
 * @param {Object} filterMetadata - Filter metadata from filterManagementSlice
 * @param {Object} filterConfig - Filter config (filterConfig.config.json)
 * @param {Object} customizations - Custom filter names from filterManagementSlice
 * @param {Array} stateAndHealthFilters - [stateFilter, healthEquityFilter] from breadcrumbs
 * @param {Object} outreachData - Outreach data from useOutreachFilters hook
 * @returns {Array} Array of chip objects for PDF display
 */
export const getFormatedDataForChip = (
  appliedFilters,
  filterMetadata,
  filterConfig,
  customizations,
  stateAndHealthFilters,
  outreachData
) => {
  // Return empty if no state/health filters
  if (!stateAndHealthFilters || stateAndHealthFilters?.length === 0) return []

  const chipValues = []

  // Add state chips
  const selectedStates = getSelectedStates(stateAndHealthFilters[0])
  if (selectedStates) {
    chipValues.push(...selectedStates)
  }

  // Process applied filters
  if (!isEmpty(appliedFilters) && filterMetadata) {
    for (const [key, value] of Object.entries(appliedFilters)) {
      const metadata = filterMetadata[key]
      if (!metadata) continue

      const chips = processFilter(key, value, metadata, filterConfig, customizations)
      chipValues.push(...chips)
    }
  }

  // Add outreach chips
  updateOutreachSimulationSelectedChipsIfReq(outreachData, chipValues)

  return chipValues
}

/**
 * Update chip values with outreach simulation data if applicable
 * Uses new filter management system data format
 *
 * @param {Object} outreachData - Outreach data from useOutreachFilters hook
 *   Format: { isActive, type, enrolledValue, selectedOptions, activeFilterOptions, params }
 * @param {Array} chipValues - Array to push chip objects to
 */
export const updateOutreachSimulationSelectedChipsIfReq = (outreachData, chipValues) => {
  if (!outreachData?.isActive) return

  const {selectedOptions, enrolledValue, activeFilterOptions} = outreachData

  if (!selectedOptions || selectedOptions.length === 0) return

  // Get enrolled label
  const enrolledLabel = enrolledOptions.find((opt) => opt.value === enrolledValue)?.label || 'All'

  // Add chip for each selected option
  for (const optionValue of selectedOptions) {
    // Find the full option config to get the label
    const optionConfig = activeFilterOptions?.find((opt) => opt.value === optionValue)
    const optionLabel = optionConfig?.label || optionValue

    chipValues.push({
      type: 'checkbox',
      label: optionLabel,
      values: enrolledLabel
    })
  }
}

/**
 * List of functions and their purpose:
 *
 * - getSelectedStates: Retrieves the selected states and formats them for display.
 * - getCustomHeName: Retrieves the custom health equity name for a given value.
 * - getFilterName: Gets filter display name from customizations (API), admin settings, or config.
 * - getToggleLabel: Converts toggle value to Yes/No/All label.
 * - isEmpty: Checks if an object is empty.
 * - getFormatedDataForChip: Formats data into chip values for display using new filter management system.
 * - updateOutreachSimulationSelectedChipsIfReq: Updates chip values with outreach simulation data if applicable.
 */
